import { fabric } from 'fabric';
import { ALIGN_LABEL, DISTRIBUTE_LABEL, LAYER_LABEL, STYLE_KIND_LABEL, styleProblem, type Align, type Axis, type LayerMove } from '../editor/arrange';
import type { Editor } from '../editor/editor';
import { $ } from './dom';
import type { GroupSelect } from './groupSelect';
import type { ObjectActions } from './objectActions';
import { kindName } from './objects';
import { toast } from './toast';

/*
 * The object menu (คลิกขวา on a computer, แตะสองนิ้วพร้อมกัน on iPad — src/editor/editor.ts):
 *   on an object      copy · style ›  · paste · duplicate · delete | layer › · จัดองค์ประกอบ › (align, spread evenly) |
 *                     จัดกลุ่ม ⌘G · ยกเลิกการรวมกลุ่ม ⇧⌘G | fit page · lock |
 *                     as background (picture) · colour to background · download · info
 *   on a locked one   unlock · info
 *   on empty page     paste · select all
 * A sub-menu replaces the list (‹ back): easier to hit with a finger than one that flies out.
 * An item that cannot be used now stays visible, greyed, and says why when tapped (never a silent no-op).
 */

type Obj = fabric.Object & { locked?: boolean };
interface Item { label: string; icon: string; key?: string; act?: () => string | null | Promise<string | null>; sub?: () => Entry[]; off?: string | null; danger?: boolean }
type Entry = Item | 'sep';

const svg = (d: string) => `<svg viewBox="0 0 24 24">${d}</svg>`;
const I = {
  copy: svg('<rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V5a1 1 0 00-1-1H5a1 1 0 00-1 1v10a1 1 0 001 1h3"/>'),
  style: svg('<rect x="4" y="3" width="14" height="6" rx="1.5"/><path d="M18 6h2v5h-8v3"/><rect x="10" y="14" width="4" height="7" rx="1"/>'),
  paste: svg('<rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4V3h6v1M9 9h6M9 13h6"/>'),
  dup: svg('<rect x="8" y="8" width="12" height="12" rx="2"/><path d="M14 11v6M11 14h6M16 8V5a1 1 0 00-1-1H5a1 1 0 00-1 1v10a1 1 0 001 1h3"/>'),
  del: svg('<path d="M5 7h14M10 11v6M14 11v6M7 7l1 12h8l1-12M9 7V4h6v3"/>'),
  layers: svg('<path d="M12 3l9 5-9 5-9-5 9-5z"/><path d="M3 13l9 5 9-5"/>'),
  align: svg('<path d="M4 3v18"/><rect x="7" y="6" width="9" height="4" rx="1"/><rect x="7" y="14" width="13" height="4" rx="1"/>'),
  spreadH: svg('<path d="M3 4v16M21 4v16"/><rect x="9" y="8" width="6" height="8" rx="1"/>'),
  spreadV: svg('<path d="M4 3h16M4 21h16"/><rect x="8" y="9" width="8" height="6" rx="1"/>'),
  group: svg('<rect x="3" y="3" width="18" height="18" rx="2" stroke-dasharray="3 2"/><rect x="7" y="7" width="6" height="6" rx="1"/><rect x="11" y="11" width="6" height="6" rx="1"/>'),
  ungroup: svg('<rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/>'),
  fit: svg('<path d="M3 12h18M6 9l-3 3 3 3M18 9l3 3-3 3"/>'),
  lock: svg('<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/>'),
  unlock: svg('<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 017.5-2"/>'),
  bg: svg('<path d="M4 20L20 4M4 14L14 4M4 8l4-4M10 20L20 10M16 20l4-4"/>'),
  color: svg('<path d="M12 3a9 9 0 100 18c1.1 0 1.5-.8 1.5-1.6 0-.9-.6-1.4-.6-2.2 0-1 .8-1.7 1.8-1.7H17a4 4 0 004-4c0-4.7-4-8.5-9-8.5z"/><circle cx="7.5" cy="11" r="1"/><circle cx="10" cy="7" r="1"/><circle cx="15" cy="7.5" r="1"/>'),
  down: svg('<path d="M12 4v11M7 10l5 5 5-5M5 20h14"/>'),
  info: svg('<circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/>'),
  all: svg('<rect x="4" y="4" width="16" height="16" rx="2" stroke-dasharray="3 2"/>'),
  back: svg('<path d="M15 6l-6 6 6 6"/>'),
  more: svg('<path d="M9 6l6 6-6 6"/>')
};

export function initContextMenu(ed: Editor, actions: ObjectActions, groups: GroupSelect, opts: { canEdit: () => boolean }) {
  const { canvas } = ed;
  const box = $('ocMenu');
  let open = false;
  /** where the menu was asked for (CSS px over the canvas), kept for sub-menus */
  let at = { x: 0, y: 0 };

  function close() {
    if (!open) return;
    open = false; box.hidden = true; box.textContent = '';
  }

  /** the object under a point (CSS px over the canvas): the chosen group when inside it, else the top-most, locked ones too */
  function hit(x: number, y: number): Obj | null {
    const p = new fabric.Point(x, y);
    const act = canvas.getActiveObject();
    if (act && act.containsPoint(p)) return act as Obj;
    const list = ed.userObjects().filter(o => o.visible !== false);
    for (let k = list.length - 1; k >= 0; k--) {
      const o = list[k]; o.setCoords();
      if (o.containsPoint(p)) return o as Obj;
    }
    return null;
  }

  const ro = () => opts.canEdit() ? null : 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว';

  function objectItems(o: Obj): Entry[] {
    const many = o.type === 'activeSelection';
    if (o.locked) return [
      { label: 'ปลดล็อก', icon: I.unlock, act: () => actions.unlock(o), off: ro() },
      'sep',
      { label: 'ข้อมูล', icon: I.info, sub: () => infoItems(o) }
    ];
    const pic = !many && o.type === 'image';
    const s = actions.styleToPaste();
    return [
      { label: 'คัดลอก', icon: I.copy, key: '⌘C', act: actions.copy },
      { label: 'รูปแบบการคัดลอก', icon: I.style, sub: () => [
        { label: 'คัดลอกรูปแบบ', icon: I.copy, act: actions.copyStyle, off: many ? 'คัดลอกรูปแบบได้ทีละชิ้น' : null },
        { label: s ? `วางรูปแบบ (ของ${STYLE_KIND_LABEL[s.kind]})` : 'วางรูปแบบ', icon: I.paste, act: actions.pasteStyle,
          off: ro() ?? (!s ? 'ยังไม่ได้คัดลอกรูปแบบ' : many ? null : styleProblem(s, o.type)) }
      ] },
      { label: 'วาง', icon: I.paste, key: '⌘V', act: actions.paste, off: ro() ?? (actions.hasClip() ? null : 'ยังไม่ได้คัดลอกอะไรในแอปนี้') },
      { label: 'ทำสำเนา', icon: I.dup, key: '⌘D', act: actions.duplicate, off: ro() },
      { label: 'ลบ', icon: I.del, key: 'DELETE', act: actions.remove, off: ro(), danger: true },
      'sep',
      { label: 'เลเยอร์', icon: I.layers, sub: () => (['front', 'forward', 'backward', 'back'] as LayerMove[]).map(h => (
        { label: LAYER_LABEL[h], icon: I.layers, act: () => actions.layer(h), off: ro() ?? (many && (h === 'forward' || h === 'backward') ? 'ใช้ได้ทีละชิ้น' : null) })) },
      { label: 'จัดองค์ประกอบ', icon: I.align, sub: () => {
        // a group counts as one piece: a chosen group alone lines up on the page
        const n = actions.pieces();
        return [
          { label: n > 1 ? 'จัดตำแหน่ง (เทียบกันเอง)' : 'จัดตำแหน่ง (บนหน้า)', icon: I.info },
          ...(['left', 'hcenter', 'right', 'top', 'vcenter', 'bottom'] as Align[]).map(h => ({ label: ALIGN_LABEL[h], icon: I.align, act: () => actions.align(h), off: ro() })),
          'sep',
          ...(['h', 'v'] as Axis[]).map(a => ({ label: DISTRIBUTE_LABEL[a], icon: a === 'h' ? I.spreadH : I.spreadV, act: () => actions.distribute(a),
            off: ro() ?? (n < 3 ? `ต้องเลือกอย่างน้อย 3 ชิ้น (กลุ่มนับเป็น 1 ชิ้น) ตอนนี้เลือกไว้ ${n} ชิ้น` : null) }))
        ];
      } },
      'sep',
      ...groupItems(),
      'sep',
      { label: 'ปรับขนาดหน้าให้พอดีกับที่เลือก', icon: I.fit, act: actions.fitPage, off: ro() ?? (ed.doc.video ? 'งานวิดีโอมีขนาดตายตัว' : null) },
      { label: 'ล็อก', icon: I.lock, act: actions.lock, off: ro() },
      'sep',
      ...(pic ? [{ label: 'กำหนดรูปภาพเป็นพื้นหลัง', icon: I.bg, act: actions.asBackground, off: ro() ?? (ed.doc.video ? 'บนวิดีโอใช้ไม่ได้' : null) } as Item] : []),
      { label: 'ใช้สีนี้เป็นพื้นหลัง', icon: I.color, act: actions.colorToPage, off: ro() ?? (many ? 'ใช้สีได้ทีละชิ้น' : null) },
      { label: 'ดาวน์โหลดที่เลือก (PNG พื้นใส)', icon: I.down, act: actions.downloadChosen },
      { label: 'ข้อมูล', icon: I.info, sub: () => infoItems(o), off: many ? 'เลือกทีละชิ้นเพื่อดูข้อมูล' : null }
    ];
  }

  /** จัดกลุ่ม when several are chosen (greyed with the reason when they already are one group); ยกเลิกการรวมกลุ่ม when a group is chosen */
  function groupItems(): Item[] {
    const state = actions.groupState(), list = actions.chosen();
    const out: Item[] = [];
    if (list.length > 1) out.push({ label: 'จัดกลุ่ม', icon: I.group, key: '⌘G', act: actions.group, off: ro() ?? (state === 'group' ? null : 'ชิ้นที่เลือกเป็นกลุ่มเดียวกันอยู่แล้ว') });
    if (state === 'ungroup' || list.some(o => (o as { groupId?: string }).groupId)) out.push({ label: 'ยกเลิกการรวมกลุ่ม', icon: I.ungroup, key: '⇧⌘G', act: actions.ungroup, off: ro() });
    return out;
  }

  function infoItems(o: Obj): Item[] {
    const head: Item = { label: o.locked ? `${kindName(o) ?? 'ชิ้นงาน'} (ล็อกอยู่)` : kindName(o) ?? 'ชิ้นงาน', icon: I.info };
    return [head, ...actions.info(o).map(t => ({ label: t, icon: '' }))];
  }

  const pageItems = (): Entry[] => [
    { label: 'วาง', icon: I.paste, key: '⌘V', act: actions.paste, off: ro() ?? (actions.hasClip() ? null : 'ยังไม่ได้คัดลอกอะไรในแอปนี้') },
    { label: 'เลือกทั้งหมดในหน้านี้', icon: I.all, act: actions.selectAll }
  ];

  /** draw a list (with "‹ กลับ" for a sub-menu) and keep it on screen */
  function paint(list: Entry[], title?: string, back?: () => void) {
    box.textContent = '';
    if (back) {
      const b = document.createElement('button');
      b.className = 'ocback'; b.innerHTML = `${I.back}<span>${title ?? 'กลับ'}</span>`;
      b.onclick = e => { e.stopPropagation(); back(); };
      box.append(b);
    }
    for (const it of list) {
      if (it === 'sep') { box.append(Object.assign(document.createElement('hr'), { className: 'ocsep' })); continue; }
      const b = document.createElement('button');
      b.className = 'ocitem' + (it.danger ? ' danger' : '') + (it.off ? ' off' : '') + (!it.act && !it.sub ? ' text' : '');
      b.setAttribute('role', 'menuitem');
      if (it.off) b.setAttribute('aria-disabled', 'true');
      b.innerHTML = `<i>${it.icon}</i><span></span>${it.key ? `<kbd>${it.key}</kbd>` : it.sub ? `<i class="ocmore">${I.more}</i>` : ''}`;
      b.querySelector('span')!.textContent = it.label;
      b.onclick = async e => {
        e.stopPropagation();
        if (it.off) { toast(it.off, true); return; }
        if (it.sub) { const sub = it.sub; paint(sub(), it.label, () => paint(current(), undefined)); place(); return; }
        if (!it.act) return;
        close();
        try { const why = await it.act(); if (why) toast(why, true); }
        catch (err) { toast(err instanceof Error ? err.message : String(err), true); }
      };
      box.append(b);
    }
  }

  let current: () => Entry[] = () => [];

  /** inside the work area, next to the point; flipped when it would go off the edge */
  function place() {
    const stage = box.parentElement as HTMLElement;
    const W = stage.clientWidth, H = stage.clientHeight, w = box.offsetWidth, h = box.offsetHeight;
    let x = at.x + 6, y = at.y + 6;
    if (x + w > W - 8) x = Math.max(8, at.x - w - 6);
    if (y + h > H - 8) y = Math.max(8, H - h - 8);
    box.style.left = `${Math.round(x)}px`; box.style.top = `${Math.round(y)}px`;
  }

  /** open the menu for what is at (x, y) — CSS px over the canvas */
  function openAt(x: number, y: number) {
    if ((canvas.getActiveObject() as fabric.IText | null)?.isEditing) { toast('กำลังแก้ข้อความอยู่ กด "เสร็จ" ก่อนแล้วค่อยเปิดเมนูชิ้นงาน', true); return; }
    const o = hit(x, y);
    // a member of a group: the whole group (as a tap does, src/ui/groupSelect.ts)
    if (o && !o.locked && o !== canvas.getActiveObject() && !groups.choose(o)) { canvas.setActiveObject(o); ed.paint(); canvas.requestRenderAll(); }
    if (!o) { canvas.discardActiveObject(); canvas.requestRenderAll(); }
    at = { x, y };
    const chosen = canvas.getActiveObject() as Obj | null;
    current = o ? () => objectItems(o.locked ? o : chosen ?? o) : pageItems;
    open = true; box.hidden = false;
    paint(current());
    place();
  }

  /* computer: right click on the work area */
  const upper = (canvas as unknown as { upperCanvasEl: HTMLCanvasElement }).upperCanvasEl;
  upper.addEventListener('contextmenu', e => {
    // text being edited: the browser's own menu (copy text, spelling) stays
    if ((canvas.getActiveObject() as fabric.IText | null)?.isEditing) return;
    e.preventDefault();
    const r = upper.getBoundingClientRect();
    openAt(e.clientX - r.left, e.clientY - r.top);
  });
  /* iPad: two fingers tapped together */
  ed.setOnTwoFingerTap((x, y) => openAt(x, y));

  /* closing: a tap/click outside, Escape, the view or the page changing */
  document.addEventListener('pointerdown', e => { if (open && !box.contains(e.target as Node)) close(); }, true);
  document.addEventListener('keydown', e => { if (open && e.key === 'Escape') { e.stopPropagation(); close(); } }, true);
  canvas.on('mouse:wheel', close);
  canvas.on('object:moving', close);
  // the objects it was opened for changed under it (undo/redo, another page, something added or removed)
  canvas.on('object:removed', close);
  canvas.on('object:added', close);

  return { openAt, close, get open() { return open; } };
}
